import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { Event } from "types/event.type";

export type ReceivedProps = {
  tasks: any;
  events?: Event[];
};

export const TAB_TIMES = [
  {
    id: "Day",
    name: "日",
  },
  {
    id: "Week",
    name: "週",
  },
  {
    id: "Month",
    name: "月",
  },
];

export const ITEM_TAB_LIST = {
  id: "List",
  name: "リスト",
};

export const SWITCHER_LIST = [...TAB_TIMES, ITEM_TAB_LIST];

export interface CustomizedState {
  editingId: number;
}

const useSchedulerBox = (props: ReceivedProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewNameParam = searchParams.get("viewName") as string;
  const isValidViewName = SWITCHER_LIST.some((x) => x.id == viewNameParam);
  const [showDayList, setShowDayList] = useState<boolean>(
    isValidViewName && ITEM_TAB_LIST.id === viewNameParam,
  );
  const [currentViewName, setCurrentViewName] = useState<string>(
    isValidViewName ? viewNameParam : "Month",
  );
  const [currentDate, setCurrentDate] = useState<any>(new Date());
  const [localScheduleDate, setLocalScheduleData] = useState<any[]>();
  const [scheduleList, setScheduleList] = useState<any>({
    data: [],
  });

  const commitChanges = () => {};
  const eventData = useMemo(() => {
    return (props.events || []).map((ev) => {
      let startDate = moment(ev.start_date).toISOString();
      let endDate = moment(ev.end_date).toISOString();
      let titlePrefix;
      if (ev.schedule_duration_type === "all_day") {
        titlePrefix = `${moment(ev.start_date).format("DD")}日 - ${moment(
          ev.end_date,
        ).format("DD")}日`;
        startDate = moment(ev.start_date).startOf("day").toISOString();
        endDate = moment(ev.end_date).endOf("day").toISOString();
      } else if (ev.schedule_duration_type === "specific_date_time") {
        titlePrefix = `${moment(ev.start_date).format("HH:mm")}`;

        // library dx-react-scheduler don't show when start = end;
        if (moment(ev?.start_date).isSame(moment(ev?.end_date))) {
          endDate = moment(ev?.end_date)
            .set("minute", moment(ev?.end_date).get("minute") + 1)
            .toISOString();
        }
      }
      return {
        ...ev,
        title: `${titlePrefix} ${ev.title}`,
        originalTitle: ev.title,
        dataType: "event",
        start: startDate,
        end: endDate,
        startDate,
        endDate,
        allDay: ev.schedule_duration_type === "all_day",
        styles: {
          progressColor: ev.schedule_color || "#ed8077",
        },
      };
    });
  }, [props.events]);

  const fetchSchedules = useCallback(async () => {
    const result = props.tasks
      .map((item: any, index: number) => ({
        ...item,
        id: index + 1,
        taskId: item.id,
        title: item.name,
        startDate: moment(item.start).toISOString(),
        endDate: moment(item.end).toISOString(),
      }))
      .concat(eventData);
    setScheduleList({
      data: result.sort((d1: any, d2: any) => {
        if (!d1.startDate && !d2.startDate) return 0;
        if (!d1.startDate) return 1;
        if (!d2.startDate) return -1;

        const date1 = new Date(d1.startDate);
        const date2 = new Date(d2.startDate);

        if (isNaN(date1.getTime()) && isNaN(date2.getTime())) return 0;
        if (isNaN(date1.getTime())) return 1;
        if (isNaN(date2.getTime())) return -1;

        return date1.getTime() - date2.getTime();
      }),
    });
  }, [props.tasks, eventData]);

  useEffect(() => {
    if (currentViewName === "Month") {
      let counting = 0;
      let prevItemDate: any = null;

      let schedulerDataForMonth: any = [];

      scheduleList.data.forEach((item: any) => {
        const currentItemDate = moment(item.startDate).format("YYYY-MM-DD");

        if (currentItemDate === prevItemDate && counting < 2) {
          counting += 1;
          schedulerDataForMonth = [
            ...schedulerDataForMonth,
            {
              ...item,
              title:
                item.dataType === "event"
                  ? item.title
                  : `${item?.project?.name || ""}-${item.title}`,
            },
          ];
        } else if (currentItemDate === prevItemDate && counting === 2) {
          counting += 1;

          const getCurrentDateLength = scheduleList.data.filter(
            (childItem: any) =>
              moment(childItem.startDate).format("YYYY-MM-DD") ===
              currentItemDate,
          );
          schedulerDataForMonth = [
            ...schedulerDataForMonth,
            {
              ...item,
              title: `他の${getCurrentDateLength.length - 3}件`,
              showWeekView: true,
            },
          ];
        } else if (currentItemDate !== prevItemDate) {
          counting = 0;
          prevItemDate = moment(item.startDate).format("YYYY-MM-DD");
          schedulerDataForMonth = [
            ...schedulerDataForMonth,
            {
              ...item,
              title:
                item.dataType === "event"
                  ? item.title
                  : `${item?.project?.name || ""}-${item.title}`,
            },
          ];
        }
      });
      setLocalScheduleData(schedulerDataForMonth);
    } else {
      setLocalScheduleData(scheduleList.data);
    }
  }, [scheduleList.data, currentViewName]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return {
    ...props,
    showDayList,
    currentViewName,
    currentDate,
    localScheduleDate,
    scheduleList,
    fetchSchedules,
    commitChanges,
    setLocalScheduleData,
    setCurrentDate,
    setShowDayList,
    setCurrentViewName,
    setSearchParams,
  };
};

export type Props = ReturnType<typeof useSchedulerBox>;

export default useSchedulerBox;
